import api from "./client";

export const userApi = {
  create: (body) => api.post("/user", body),
  update: (id, body) => api.put(`/user/id/${id}`, body),
  byEmail: (email) => api.get(`/user/email/${email}`),
  byId: (id) => api.get(`/user/id/${id}`),
};

export const athleteProfileApi = {
  create: (body) => api.post("/athleteprofile", body),
  update: (userId, body) => api.put(`/athleteprofile/user/${userId}`, body),
  byUser: (userId) => api.get(`/athleteprofile/user/${userId}`),
};

export const workoutPlanApi = {
  create: (body) => api.post("/workoutplans", body),
  update: (planId, body) => api.put(`/workoutplans/${planId}`, body),
  delete: (planId) => api.delete(`/workoutplans/${planId}`),
  byPlanId: (planId) => api.get(`/workoutplans/${planId}`),
  byUser: (userId) => api.get(`/workoutplans/user/${userId}`),
  current: (userId) => api.get(`/workoutplans/user/${userId}/current`),
  week: (userId, weekIndex) => api.get(`/workoutplans/user/${userId}/week/${weekIndex}`),
};

export const philosophyApi = {
  create: (body) => api.post("/philosophies", body),
  update: (distanceType, id, body) =>
    api.put(`/philosophies/${distanceType}/${id}`, body),
  delete: (distanceType, id) => api.delete(`/philosophies/${distanceType}/${id}`),
  all: () => api.get("/philosophies"),
  byDistance: (distanceType) => api.get(`/philosophies/${distanceType}`),
  byId: (distanceType, id) => api.get(`/philosophies/${distanceType}/${id}`),
};

export const personaApi = {
  create: (body) => api.post("/personas", body),
  update: (personaId, version, body) => api.put(`/personas/${personaId}/${version}`, body),
  delete: (personaId, version) => api.delete(`/personas/${personaId}/${version}`),
  all: () => api.get("/personas"),
  latest: (personaId) => api.get(`/personas/${personaId}`),
  catalog: () => api.get("/personas/catalog"),
};

export const globalWorkoutApi = {
  create: (body) => api.post("/workouts", body),
  update: (id, body) => api.put(`/workouts/${id}`, body),
  delete: (id) => api.delete(`/workouts/${id}`),
  latest: (id) => api.get(`/workouts/${id}`),
  version: (id, version) => api.get(`/workouts/${id}/v/${version}`),
  versions: (id) => api.get(`/workouts/${id}/versions`),
  byTag: (tag) => api.get(`/workouts`, { params: { tag } }),
};

export const personalizedWorkoutApi = {
  create: (body) => api.post("/personalizedworkouts", body),
  update: (id, body) => api.put(`/personalizedworkouts/${id}`, body),
  delete: (id) => api.delete(`/personalizedworkouts/${id}`),
  latest: (id) => api.get(`/personalizedworkouts/${id}`),
  version: (id, version) => api.get(`/personalizedworkouts/${id}/v/${version}`),
  versions: (id) => api.get(`/personalizedworkouts/${id}/versions`),
  byUser: (userId) => api.get(`/personalizedworkouts/user/${userId}`),
  byGlobal: (globalWorkoutId) =>
    api.get(`/personalizedworkouts/global/${globalWorkoutId}`),
};

export const conversationApi = {
  add: (body) => api.post("/conversation", body),
  byUser: (userId) => api.get(`/conversation/user/${userId}`),
};

export const promptRunsApi = {
  byUser: (userId, params) => api.get(`/promptruns/${userId}`, { params }),
};

export const ongoingJobsApi = {
  byStatus: (status, limit) =>
    api.get("/jobs", { params: { status, ...(limit ? { limit } : {}) } }),
  byGroup: (groupId) => api.get(`/jobs/${groupId}`),
};

export const nudgesApi = {
  byUser: (userId) => api.get("/nudges", { params: { userId } }),
};

export const smsConversationsApi = {
  byUser: (userId) => api.get("/sms/conversations", { params: { userId } }),
};

export const smsMessagesApi = {
  add: (body) => api.post("/sms/messages", body),
  byUserSinceEpoch: (userId, sinceEpochMs) =>
    api.get("/sms/messages", { params: { userId, sinceEpochMs } }),
};

export const smsPendingApi = {
  byUser: (userId) => api.get("/sms/pending", { params: { userId } }),
};

export const feedbackApi = {
  create: (body) => api.post("/feedback", body),
  list: (params) => api.get("/feedback", { params }),
  byId: (feedbackId) => api.get(`/feedback/${feedbackId}`),
  update: (feedbackId, body) => api.patch(`/feedback/${feedbackId}`, body),
};

export const analyticsApi = {
  add: (body) => api.post("/analytics", body),
  byUser: (userId) => api.get(`/analytics/user/${userId}`),
  byWorkout: (workoutId) => api.get(`/analytics/workout/${workoutId}`),
  opsSummary: (params) => api.get("/analytics/ops/summary", { params }),
};

export const stravaApi = {
  saveToken: (userId, body) => api.post(`/strava/token/${userId}`, body),
  deleteToken: (userId) => api.delete(`/strava/token/${userId}`),
  fetch: (userId) => api.post(`/strava/fetch/${userId}`),
};

export const manualWorkoutsApi = {
  create: (body) => api.post("/manualworkouts", body),
  byUser: (userId) => api.get(`/manualworkouts/user/${userId}`),
  byId: (userId, id) => api.get(`/manualworkouts/user/${userId}/${id}`),
  update: (userId, id, body) => api.put(`/manualworkouts/user/${userId}/${id}`, body),
  delete: (userId, id) => api.delete(`/manualworkouts/user/${userId}/${id}`),
};

export const adaptationApi = {
  trigger: (userId) => api.post(`/adaptation/${userId}`),
};

export const killSwitchApi = {
  get: () => api.get("/killswitch"),
  update: (body) => api.put("/killswitch", body),
};

export const messagingEndpointsApi = {
  listByUser: (userId) => api.get("/messaging/endpoints", { params: { userId } }),
  create: (body) => api.post("/messaging/endpoints", body),
  update: (userId, endpointId, body) => api.put(`/messaging/endpoints/${userId}/${endpointId}`, body),
  remove: (userId, endpointId) => api.delete(`/messaging/endpoints/${userId}/${endpointId}`),
};

export const onboardingApi = {
  getMe: () => api.get("/onboarding/me"),
  saveMe: (body) => api.put("/onboarding/me", body),
  redeemAccessCode: (body) => api.post("/onboarding/access-code", body),
  jobStatus: (groupId) => api.get(`/onboarding/jobs/${groupId}`),
  createTelegramLink: () => api.post("/onboarding/telegram/link"),
  telegramStatus: () => api.get("/onboarding/telegram/status"),
};

export const waitlistApi = {
  join: (body) => api.post("/waitlist", body),
};

export const meApi = {
  get: () => api.get("/me"),
  updateUser: (body) => api.put("/me/user", body),
  updateAthlete: (body) => api.put("/me/athlete", body),
  philosophies: () => api.get("/me/philosophies"),
  personas: () => api.get("/me/personas"),
  planOptions: () => api.get("/me/plan-options"),
  createWorkoutPlan: (body) => api.post("/me/workoutplans", body),
  setCurrentWorkoutPlan: (body) => api.put("/me/workoutplans/current", body),
  completeWorkoutPlan: (planId, completed) => api.post(`/me/workoutplans/${planId}/complete`, { completed }),
  week: (planId, weekIndex) => api.get(`/me/workoutplans/${planId}/week/${weekIndex}`),
  saveWorkoutPreference: (body) => api.post("/me/workouts/preference", body),
  completedWorkouts: (params) => api.get("/me/completed-workouts", { params }),
  createManualWorkout: (body) => api.post("/me/manual-workouts", body),
  updateManualWorkout: (manualEntryId, body) => api.put(`/me/manual-workouts/${manualEntryId}`, body),
  deleteManualWorkout: (manualEntryId) => api.delete(`/me/manual-workouts/${manualEntryId}`),
  adaptationCheck: () => api.post("/me/adaptation/check"),
  adaptationTrigger: () => api.post("/me/adaptation/trigger"),
  jobStatus: (groupId) => api.get(`/me/jobs/${groupId}`),
};
